import React, { useEffect, useState } from "react";
import Select from "react-select";
import moment from "moment-timezone";
import { toast } from "react-toastify";

import CountryTimeZoneSelection from "../profile/countryTimeZoneSelection";
import { getTimeZoneFromCountry } from "../../services/auth";

// Lean version of the country/timezone sanity check used in
// guestbooking/bookingpage.js (same reference lists).
const US_TIMEZONES = [
  "(GMT-5:00) Eastern Standard Time - New York",
  "(GMT-5:00) Eastern Standard Time - East-Indiana",
  "(GMT-6:00) Central Standard Time - Chicago",
  "(GMT-9:00) Alaska Standard Time - Alaska",
  "(GMT-10:00) Hawaii Standard Time - Honolulu",
  "(GMT-11:00) Samoa Standard Time - Midway",
  "(GMT-06:00) Central Standard Time - Chicago",
  "(GMT-05:00) Eastern Standard Time - New_York",
];
const CANADA_TIMEZONES = [
  "(GMT-3:30) Newfoundland Standard Time - Newfoundland",
  "(GMT-4:00) Atlantic Standard Time - Atlantic",
  "(GMT-5:00) Eastern Standard Time - New York",
  "(GMT-6:00) Central Standard Time - Saskatchewan",
  "(GMT-6:00) Central Standard Time - Chicago",
  "(GMT-06:00) Central Standard Time - Chicago",
];

const STEPS = ["Timezone", "Events", "Register", "Payment"];

export const StepIndicator = ({ active }) => (
  <div className="se-steps">
    {STEPS.map((label, index) => (
      <React.Fragment key={label}>
        {index > 0 && <span className="se-step-line" />}
        <div className={`se-step ${index + 1 === active ? "se-step-active" : ""}`}>
          <span className="se-step-circle">{index + 1}</span>
          <span className="se-step-label">{label}</span>
        </div>
      </React.Fragment>
    ))}
  </div>
);

export default function TimezoneGate({ countryData, timezoneData, selection, onNext }) {
  const [countryValue, setCountryValue] = useState(selection.countryValue || "");
  const [timeZoneValue, setTimeZoneValue] = useState(selection.timeZoneValue || "");

  // "You've indicated that you are in United States/Canada. Can you please
  // reconfirm your timezone?" — same reconfirm popup guest-booking uses
  // (profile/countryTimeZoneSelection.js), reused as-is so its behavior
  // stays identical between the two flows.
  const [countrySpecifiedTimeZone, setCountrySpecifiedTimeZone] = useState([]);
  const [reconfirmCountryName, setReconfirmCountryName] = useState("");
  const [currentDate] = useState(moment().format("hh:mm A"));

  // Default the timezone from the browser when nothing was chosen yet
  useEffect(() => {
    if (!timeZoneValue && timezoneData.length) {
      const guess = moment.tz.guess();
      const match = timezoneData.find((item) => item.name === guess);
      if (match) setTimeZoneValue(match.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneData]);

  const countryOptions = countryData.map((item) => ({
    value: item.id,
    label: item.country_name,
    country_code: item.country_code,
    dot_name: item.country_code.split("").join("."),
    updatedName:
      item.country_name === "United States"
        ? "USA"
        : item.country_name
            .split(" ")
            .map((word) => word.charAt(0))
            .join(""),
    updatedDotName:
      item.country_name === "United States"
        ? "U.S.A."
        : item.country_name
            .split(" ")
            .map((word) => word.charAt(0))
            .join(".")
            .concat("."),
  }));

  const countryFilterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const input = inputValue.toLowerCase();
    const { label, data } = option;
    const country_code = (data.country_code || "").toLowerCase();
    const dot_name = (data.dot_name || "").toLowerCase();
    const updatedName = (data.updatedName || "").toLowerCase();
    const updatedDotName = (data.updatedDotName || "").toLowerCase();
    return (
      label.toLowerCase().startsWith(input) ||
      country_code.startsWith(input) || country_code.includes(input) || country_code.endsWith(input) ||
      dot_name.startsWith(input) || dot_name.includes(input) || dot_name.endsWith(input) ||
      updatedName.startsWith(input) || updatedName.includes(input) || updatedName.endsWith(input) ||
      updatedDotName.startsWith(input) || updatedDotName.includes(input) || updatedDotName.endsWith(input)
    );
  };
  const timezoneOptions = timezoneData.map((item) => ({
    value: item.id,
    label: item.front_name,
  }));

  const handleNext = () => {
    if (!timeZoneValue || !countryValue) {
      toast.error("Please select both Timezone and Country", {
        position: "top-center",
      });
      return;
    }
    const country = countryData.find((item) => item.id === parseInt(countryValue));
    const timezone = timezoneData.find((item) => item.id === parseInt(timeZoneValue));
    if (!country || !timezone) return;

    if (
      country.country_name === "United States" &&
      !US_TIMEZONES.includes(timezone.front_name)
    ) {
      openCountryTimezoneReconfirm(country);
      return;
    }
    if (
      country.country_name === "Canada" &&
      !CANADA_TIMEZONES.includes(timezone.front_name)
    ) {
      openCountryTimezoneReconfirm(country);
      return;
    }

    onNext({
      countryValue: country.id,
      countryName: country.country_name,
      countryCode: country.country_code,
      timeZoneValue: timezone.id,
      timeZoneName: timezone.front_name,
      timeZoneLocalName: timezone.name,
    });
  };

  // Country selected is US/Canada but the chosen timezone isn't one of the
  // standard continental zones for that country — ask the guest to
  // reconfirm from a country-specific timezone list, same as guest-booking.
  const openCountryTimezoneReconfirm = (country) => {
    setReconfirmCountryName(country.country_name);
    setCountrySpecifiedTimeZone([]);
    window.loadModal2("#CountryTimeZoneSelection", "show");
    getTimeZoneFromCountry(country.country_code)
      .then((data) => {
        if (data && data.data && data.data.status) {
          setCountrySpecifiedTimeZone(data.data.data);
        }
      })
      .catch(() => {});
  };

  // Called by the popup once the guest picks/confirms a timezone from the
  // country-specific list. That choice is treated as final — same as
  // guest-booking — so we go straight to the next step.
  const confirmReconfirmedTimeZone = (value) => {
    const chosen = countrySpecifiedTimeZone.find(
      (item) => item.id === parseInt(value)
    );
    window.loadModal2("#CountryTimeZoneSelection", "hide");
    // The step change right after this unmounts the popup before Bootstrap's
    // own fade-out finishes, which otherwise leaves its .modal-backdrop
    // stuck on the page (the dimmed overlay), and its "modal-open" class +
    // inline padding-right stuck on <body> (blocks scrolling until a
    // refresh). Force both off immediately — same fix used elsewhere in the
    // app that hides a modal right before navigating away (see
    // bookingLinkModal.js closeHandler and medium/appointments/index.js).
    window.removeBackdrop();
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    if (!chosen) return;

    const country = countryData.find((item) => item.id === parseInt(countryValue));
    if (!country) return;

    setTimeZoneValue(chosen.id);
    onNext({
      countryValue: country.id,
      countryName: country.country_name,
      countryCode: country.country_code,
      timeZoneValue: chosen.id,
      timeZoneName: chosen.front_name,
      timeZoneLocalName: chosen.name,
    });
  };

  return (
    <div className="container se-gate">
      <StepIndicator active={1} />
      <div className="row">
        <div className="col-12 text-center mb-4">
          <h5 className="font-weight-bold se-gate-heading">
            Please select your Timezone and Country to continue
          </h5>
          <p className="se-muted">
            This helps us show you accurate availability and appointment timings.
          </p>
        </div>
      </div>
      <div className="row justify-content-center mt-3">
        <div className="col-12 col-md-6 mb-4">
          <label className="font-weight-bold">Country</label>
          <Select
            className="custom-select-box"
            classNamePrefix="custom-select"
            options={countryOptions}
            placeholder="Select Country"
            value={countryOptions.find((opt) => opt.value === parseInt(countryValue)) || null}
            onChange={(selected) => setCountryValue(selected.value)}
            isSearchable={true}
            closeMenuOnSelect={true}
            filterOption={countryFilterOption}
          />
        </div>
        <div className="col-12 col-md-6 mb-3">
          <label className="font-weight-bold">Timezone</label>
          <Select
            className="custom-select-box"
            classNamePrefix="custom-select"
            options={timezoneOptions}
            placeholder="Select Timezone"
            value={timezoneOptions.find((opt) => opt.value === parseInt(timeZoneValue)) || null}
            onChange={(selected) => setTimeZoneValue(selected.value)}
            isSearchable={true}
            closeMenuOnSelect={true}
          />
        </div>
        <div className="col-12 text-center mt-3">
          <button type="button" className="se-btn-tan" onClick={handleNext}>
            Next
          </button>
        </div>
      </div>
      <CountryTimeZoneSelection
        confirmNewTimeZone={confirmReconfirmedTimeZone}
        newTimezoneData={countrySpecifiedTimeZone}
        CurrentDate={currentDate}
        countryName={reconfirmCountryName}
      />
    </div>
  );
}
